import type { Command } from "../document/commands.ts";
import { HISTORY_CAP } from "../document/history.ts";
import { EMPTY_TILE } from "../document/packed-tile.ts";
import type { LayerDocument, WorldDocument } from "../document/types.ts";
import type { EditEntry, EditPart, LiveSession, SettledOp } from "./session-types.ts";

/**
 * The tab's own history inside its session (ADR-086 decision 27): the edits it made, each an edit group with
 * the commands its operations resolved to, kept for undo and redo. Only the tab's own edits ever enter it; a
 * remote operation never does. The commands of an operation are the ones it showed with until its answer
 * arrives, and from then on the ones the server's order gave it, whose before-values are the true ones.
 */

/**
 * The entries of a list with one operation's commands replaced.
 *
 * @param entries The entries.
 * @param change How a part changes; the part itself when it does not.
 * @returns The entries, the list given when no part changed.
 */
function withParts(entries: readonly EditEntry[], change: (part: EditPart) => EditPart): readonly EditEntry[] {
  let changed = false;
  const next = entries.map((entry) => {
    const parts = entry.parts.map(change);
    if (parts.every((part, i) => part === entry.parts[i])) {
      return entry;
    }
    changed = true;
    return { ...entry, parts };
  });
  return changed ? next : entries;
}

/**
 * A session with one of its own edits recorded as the newest to undo: the oldest goes once `HISTORY_CAP` are
 * kept, and nothing is left to redo, since a new edit branches the history.
 *
 * @param session The session.
 * @param entry The edit.
 * @returns The session.
 */
export function withEdit(session: LiveSession, entry: EditEntry): LiveSession {
  return { ...session, past: capped([...session.past, entry]), future: [] };
}

/**
 * A list of edits cut to the newest `HISTORY_CAP`.
 *
 * @param entries The edits, the newest last.
 * @returns The list.
 */
export function capped(entries: readonly EditEntry[]): readonly EditEntry[] {
  return entries.length > HISTORY_CAP ? entries.slice(entries.length - HISTORY_CAP) : entries;
}

/**
 * A session's history after answers to its own operations: each answered operation's commands become the ones
 * the server's order gave it. An answer that does not say what the operation did (the server held it already)
 * leaves its commands as they are.
 *
 * @param session The session.
 * @param settled The answered operations.
 * @returns The session, the one given when its history holds none of them.
 */
export function withAnswers(session: LiveSession, settled: readonly SettledOp[]): LiveSession {
  const known = new Map(settled.flatMap((answer) => (answer.commands === null ? [] : [[answer.id, answer.commands] as const])));
  if (known.size === 0) {
    return session;
  }
  /**
   * A part with the commands its answer gave, when it has one.
   *
   * @param part The part.
   * @returns The part.
   */
  const answered = (part: EditPart): EditPart => {
    const commands = known.get(part.id);
    return commands === undefined ? part : { id: part.id, commands };
  };
  const past = withParts(session.past, answered);
  const future = withParts(session.future, answered);
  return past === session.past && future === session.future ? session : { ...session, past, future };
}

/**
 * Whether a layer holds a tile cell, which is packed against the tileset list as it stood.
 *
 * @param layer The layer.
 * @returns True for a tile layer with a cell that is not empty.
 */
function holdsTiles(layer: LayerDocument): boolean {
  return layer.kind === "tile" && layer.tiles.some((cell) => cell !== EMPTY_TILE);
}

/**
 * Whether a command carries a tile layer's cells in their packed form: the add or the removal of a tile layer
 * that is not empty, or of a map that holds one. Every other command names its tiles by tileset id.
 *
 * @param command The command.
 * @returns True when it does.
 */
function carriesTiles(command: Command): boolean {
  switch (command.kind) {
    case "compound":
      return command.commands.some(carriesTiles);
    case "addLayer":
    case "removeLayer":
      return holdsTiles(command.layer);
    case "addMap":
    case "removeMap":
      return command.map.layers.some(holdsTiles);
    default:
      return false;
  }
}

/**
 * A session's history after its shown document changed: when any tileset the document listed now stands at
 * another index or is gone from the list, the edits whose commands carry a tile layer's packed cells are
 * dropped from both lists, since those cells would come back naming other tiles. Every other edit stays: its
 * commands name tiles by tileset id and compare cells by meaning. Appending a tileset moves none.
 *
 * @param before The document shown before.
 * @param session The session after the change.
 * @returns The session, and how many edits were dropped.
 */
export function afterTilesets(before: WorldDocument, session: LiveSession): { readonly session: LiveSession; readonly dropped: number } {
  const moved = before.tilesets !== session.shown.tilesets && before.tilesets.some((tileset, index) => session.shown.tilesets[index]?.id !== tileset.id);
  if (!moved) {
    return { session, dropped: 0 };
  }
  /**
   * Whether an edit can still be taken back after the move.
   *
   * @param entry The edit.
   * @returns True when none of its commands carries packed tile cells.
   */
  const stays = (entry: EditEntry): boolean => !entry.parts.some((part) => part.commands.some(carriesTiles));
  const past = session.past.filter(stays);
  const future = session.future.filter(stays);
  const dropped = session.past.length - past.length + session.future.length - future.length;
  return dropped === 0 ? { session, dropped } : { session: { ...session, past, future }, dropped };
}
